import { formatMoney } from '@/utils/format';
import CountUp from '@/components/base/CountUp';
import type { LedgerSummary as LedgerSummaryData } from '@/utils/ledger';

interface LedgerSummaryProps {
  summary: LedgerSummaryData;
}

export default function LedgerSummary({ summary }: LedgerSummaryProps) {
  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Ledger summary">
      <article className="tile-sheen brand-cta rounded-lg p-5 text-background-50 dark:text-foreground-950" style={{ animationDelay: '0.0s' }}>
        <div className="flex items-start justify-between">
          <span className="text-xs font-label font-medium uppercase tracking-wider text-background-50/80 dark:text-foreground-900/80">
            Total transactions
          </span>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/20 text-lg">
            <i className="ri-swap-3-line" />
          </span>
        </div>
        <p className="mt-4 font-heading text-3xl font-semibold">
          <CountUp value={String(summary.count)} />
        </p>
        <p className="mt-1 text-xs text-background-50/80 dark:text-foreground-900/80">
          Single payments, batches, top-ups and fees
        </p>
      </article>

      <article className="tile-sheen rounded-lg bg-accent-600 p-5 text-background-50 dark:text-foreground-950" style={{ animationDelay: '1.2s' }}>
        <div className="flex items-start justify-between">
          <span className="text-xs font-label font-medium uppercase tracking-wider text-background-50/80 dark:text-foreground-900/80">
            Total transacted
          </span>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/20 text-lg">
            <i className="ri-wallet-3-line" />
          </span>
        </div>
        <p className="mt-4 font-heading text-2xl font-semibold">
          <CountUp value={formatMoney(summary.totalKes, 'KES')} duration={1100} />
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-50/85 dark:text-foreground-900/85">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-arrow-down-line" />
            Cr: {formatMoney(summary.creditsKes, 'KES')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-arrow-up-line" />
            Dr: {formatMoney(summary.debitsKes, 'KES')}
          </span>
        </div>
      </article>

      <article className="tile-sheen rounded-lg bg-secondary-500 p-5 text-background-50 dark:text-foreground-950" style={{ animationDelay: '2.4s' }}>
        <div className="flex items-start justify-between">
          <span className="text-xs font-label font-medium uppercase tracking-wider text-background-50/80 dark:text-foreground-900/80">
            Reconciled
          </span>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/20 text-lg">
            <i className="ri-checkbox-circle-line" />
          </span>
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <p className="font-heading text-3xl font-semibold">
              <CountUp value={String(summary.reconciled)} />
            </p>
            <p className="mt-1 text-xs text-background-50/80 dark:text-foreground-900/80">
              Matched against the rails
            </p>
          </div>
          <ShareRing share={summary.count ? summary.reconciled / summary.count : 0} />
        </div>
      </article>

      <article className="tile-sheen rounded-lg bg-foreground-900 p-5 text-background-50 dark:text-foreground-950" style={{ animationDelay: '3.5999999999999996s' }}>
        <div className="flex items-start justify-between">
          <span className="text-xs font-label font-medium uppercase tracking-wider text-background-50/80 dark:text-foreground-900/80">
            Unreconciled
          </span>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/20 text-lg">
            <i className="ri-circle-line" />
          </span>
        </div>
        <p className="mt-4 flex items-center gap-2.5 font-heading text-3xl font-semibold">
          <CountUp value={String(summary.unreconciled)} />
          {summary.attention > 0 ? (
            <span className="relative flex h-2.5 w-2.5" title={`${summary.attention} need attention`}>
              <span className="absolute inline-flex h-full w-full rounded-full bg-secondary-400 opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-secondary-400" />
            </span>
          ) : null}
        </p>
        <p className="mt-1 text-xs text-background-50/80 dark:text-foreground-900/80">
          {summary.pending} pending · {summary.attention} need attention
        </p>
      </article>
    </section>
  );
}
/** A small ring showing what share of the transactions is reconciled; it draws itself in. */
function ShareRing({ share }: { share: number }) {
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const percent = Math.round(share * 100);
  return (
    <span className="relative flex h-12 w-12 shrink-0 items-center justify-center" title={`${percent}% reconciled`}>
      <svg viewBox="0 0 44 44" className="h-12 w-12 -rotate-90" aria-hidden="true">
        <circle cx="22" cy="22" r={radius} fill="none" strokeWidth="4" className="stroke-background-50/25" />
        <circle
          cx="22"
          cy="22"
          r={radius}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          className="ring-draw stroke-background-50"
          style={{ strokeDasharray: circumference, strokeDashoffset: circumference * (1 - share) }}
        />
      </svg>
      <span className="absolute text-[11px] font-semibold">{percent}%</span>
    </span>
  );
}
